import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Scale, LayoutDashboard, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const navLinks = [
  { href: '#beranda',   label: 'Beranda' },
  { href: '#tentang',   label: 'Tentang Kami' },
  { href: '#layanan',   label: 'Layanan' },
  { href: '#mengapa',   label: 'Mengapa Kami' },
  { href: '#testimoni', label: 'Testimoni' },
  { href: '#kontak',    label: 'Kontak' },
];

export default function Navbar() {
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [active,   setActive]     = useState('#beranda');
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNav = (href) => {
    setActive(href);
    setMenuOpen(false);
    if (window.location.pathname !== '/') {
      navigate('/' + href);
    } else {
      document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <motion.nav
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'glass shadow-2xl py-3' : 'bg-transparent py-5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <motion.div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => handleNav('#beranda')}
              whileHover={{ scale: 1.03 }}
            >
              <div className="w-10 h-10 rounded-xl gold-gradient flex items-center justify-center shadow-lg">
                <Scale size={20} className="text-navy-950" strokeWidth={2.5} />
              </div>
              <div>
                <p className="font-serif font-bold text-lg leading-tight gold-text">
                  Kantor Hukum
                </p>
                <p className="text-[10px] text-navy-300 tracking-widest uppercase">
                  Pontianak &bull; Kalbar
                </p>
              </div>
            </motion.div>

            {/* Desktop Links */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => handleNav(link.href)}
                  className={`relative px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    active === link.href
                      ? 'text-gold-400'
                      : 'text-navy-200 hover:text-white'
                  }`}
                >
                  {link.label}
                  {active === link.href && (
                    <motion.span
                      layoutId="nav-indicator"
                      className="absolute bottom-0.5 left-3 right-3 h-0.5 rounded-full bg-gold-400"
                    />
                  )}
                </button>
              ))}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => { setMenuOpen(false); navigate('/blog'); }}
                className="flex items-center gap-2 ml-2 px-4 py-2.5 rounded-lg text-sm font-medium text-navy-200 hover:text-white border border-white/10 hover:border-white/30 transition-all duration-300"
              >
                <BookOpen size={15} />
                Blog
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 ml-1 px-4 py-2.5 rounded-lg text-sm font-medium text-navy-200 hover:text-white border border-white/10 hover:border-white/30 transition-all duration-300"
              >
                <LayoutDashboard size={15} />
                Dashboard
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => handleNav('#kontak')}
                className="btn-primary ml-2 text-sm px-6 py-2.5"
              >
                Konsultasi
              </motion.button>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="lg:hidden p-2 rounded-lg glass-light text-gold-400"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed top-[68px] left-0 right-0 z-40 glass shadow-2xl overflow-hidden lg:hidden"
          >
            <div className="px-4 py-4 flex flex-col gap-1">
              {navLinks.map((link, i) => (
                <motion.button
                  key={link.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.06 }}
                  onClick={() => handleNav(link.href)}
                  className={`text-left px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                    active === link.href
                      ? 'text-gold-400 bg-gold-400/10'
                      : 'text-navy-200 hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </motion.button>
              ))}
              <button
                onClick={() => { setMenuOpen(false); navigate('/blog'); }}
                className="flex items-center justify-center gap-2 mt-2 px-4 py-3 rounded-xl text-sm font-medium text-navy-200 border border-white/10 hover:bg-white/5 transition-all"
              >
                <BookOpen size={15} />
                Blog
              </button>
              <button
                onClick={() => { setMenuOpen(false); navigate('/dashboard'); }}
                className="flex items-center justify-center gap-2 mt-1 px-4 py-3 rounded-xl text-sm font-medium text-navy-200 border border-white/10 hover:bg-white/5 transition-all"
              >
                <LayoutDashboard size={15} />
                Dashboard
              </button>
              <button
                onClick={() => handleNav('#kontak')}
                className="btn-primary mt-2 justify-center text-sm"
              >
                Konsultasi Sekarang
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
